import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Icons, cx } from '../../components/ui.tsx';
import { SelectBox } from '../drive/SelectionBar.tsx';
import { BRAND, BrandIcon } from '../../components/icons.tsx';
import { ContextMenu, useContextMenu, type MenuItem } from '../../components/ContextMenu.tsx';
import { ShareLinkDialog } from '../drive/ShareLinkDialog.tsx';
import { StarButton, useStarMenuItem } from '../drive/StarButton.tsx';
import { PropertiesPanel } from '../../components/Properties.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';
import { ApiError, authedFetch } from '../../lib/api.ts';
import { PdfPreview } from '../sheets/PdfPreview.tsx';
import { startDrag } from '../drive/dragging.ts';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateInPlace, payloadFor, useDriveClipboard } from '../drive/DriveClipboard.tsx';
import { refreshFolder } from '../drive/refreshFolder.ts';
import {
  useRenameDocument,
  useTrashDocument,
  type DocumentSummary,
} from '../../lib/document-queries.ts';

/**
 * A document in a Drive listing.
 *
 * The same row as a sheet's, with the same actions in the same order, because
 * to the person using this they are two kinds of paperwork in one folder and
 * the difference should not need explaining. Where they differ — a word count
 * instead of a tab count, Word instead of Excel on the download — it is
 * because the underlying thing genuinely differs.
 */
export function DocumentRow({
  document: record,
  selected,
  onToggleSelected,
  selectionActive,
  view,
}: {
  document: DocumentSummary;
  selected?: boolean;
  onToggleSelected?: (() => void) | undefined;
  /** True while a selection is being built — see ItemRow for why. */
  selectionActive?: boolean | undefined;
  /** A row in the table, or a tile in the grid. See ItemRow. */
  view?: 'list' | 'grid';
}) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(record.name);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const menu = useContextMenu();
  const dialog = useDialog();
  const toast = useToast();
  const navigate = useNavigate();

  const rename = useRenameDocument();
  const trash = useTrashDocument();
  const clipboard = useDriveClipboard();
  const client = useQueryClient();

  /** A copy beside the original, named "… (1)". */
  async function duplicate() {
    try {
      await duplicateInPlace(payloadFor('document', record.id), record.folderId);
      await refreshFolder(client, record.folderId);
      toast.success(`"${record.name}" duplicated`);
    } catch (caught) {
      toast.error(
        'Could not duplicate that',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function commitRename() {
    const name = draft.trim();
    setRenaming(false);
    if (!name || name === record.name) {
      setDraft(record.name);
      return;
    }
    try {
      await rename.mutateAsync({ id: record.id, name });
      toast.success(`Renamed to "${name}"`);
    } catch (caught) {
      setDraft(record.name);
      toast.error(
        'Could not rename',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: `Move "${record.name}" to the trash?`,
      message: 'Nothing is destroyed — trashed documents can be restored.',
      details: [pluralise(record.words, 'word'), `Last edited ${formatRelative(record.updatedAt)}`],
      confirmLabel: 'Move to trash',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await trash.mutateAsync(record.id);
      toast.success(`"${record.name}" moved to the trash`);
    } catch (caught) {
      toast.error(
        'Could not move to the trash',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  /** One fetch of an export, with the auth header a link cannot carry. */
  async function fetchExport(format: 'pdf' | 'docx'): Promise<Blob> {
    const response = await authedFetch(`/api/documents/${record.id}/export?format=${format}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('export failed');
    return response.blob();
  }

  async function viewAsPdf() {
    setPreviewing(true);
    setPdfUrl(null);
    try {
      setPdfUrl(URL.createObjectURL(await fetchExport('pdf')));
    } catch {
      setPreviewing(false);
      toast.error(
        'Could not produce that PDF',
        'PDF conversion may be unavailable on this server.',
      );
    }
  }

  function closePreview() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPreviewing(false);
  }

  async function downloadAs(format: 'pdf' | 'docx') {
    try {
      const url = URL.createObjectURL(await fetchExport(format));
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = `${record.name}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download it', 'Please try again.');
    }
  }

  /**
   * Print through the PDF rather than the browser.
   *
   * Printing the page itself prints the application around it. The PDF is the
   * document as laid out, and the print dialog's own "Save as PDF" covers
   * anyone who wanted the file instead of paper.
   */
  async function printIt() {
    try {
      const url = URL.createObjectURL(await fetchExport('pdf'));
      const frame = window.document.createElement('iframe');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      frame.src = url;
      frame.onload = () => {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      };
      window.document.body.appendChild(frame);
    } catch {
      toast.error(
        'Could not prepare that for printing',
        'PDF conversion may be unavailable on this server.',
      );
    }
  }

  function showProperties() {
    dialog.open((close) => (
      <PropertiesPanel
        onClose={close}
        content={{
          name: record.name,
          kind: 'Document',
          icon: <BrandIcon src={BRAND.word} size={24} />,
          rows: [
            { label: 'Words', value: record.words.toLocaleString(), numeric: true },
            { label: 'Created', value: formatDateTime(record.createdAt), numeric: true },
            {
              label: 'Modified',
              value: `${formatDateTime(record.updatedAt)} (${formatRelative(record.updatedAt)})`,
              numeric: true,
            },
            { label: 'Saved revisions', value: record.revision, numeric: true },
            { label: 'Starred', value: record.isStarred ? 'Yes' : 'No' },
            { label: 'Identifier', value: record.id, numeric: true },
          ],
        }}
      />
    ));
  }

  /**
   * The link dialog, raised through the dialog service like every other one
   * here — so it lands in the same place, closes on Escape, and holds the page
   * still behind it without this component knowing how any of that works.
   */
  function showLinkDialog() {
    dialog.open((close) => (
      <ShareLinkDialog kind="DOCUMENT" targetId={record.id} name={record.name} onClose={close} />
    ));
  }

  const starItem = useStarMenuItem('document', record.id, record.name, record.isStarred);

  const menuItems: MenuItem[] = [
    starItem,
    {
      label: 'Share a link…',
      icon: <Icons.Copy />,
      onSelect: () => showLinkDialog(),
    },

    { label: 'Open', icon: <Icons.File />, onSelect: () => navigate(`/documents/${record.id}`) },
    { label: 'View as PDF', icon: <Icons.Eye />, onSelect: () => void viewAsPdf() },
    { label: 'Print', icon: <PrinterGlyph />, shortcut: 'Ctrl+P', onSelect: () => void printIt() },
    {
      label: 'Download as Word',
      icon: <Icons.Download />,
      separatorBefore: true,
      onSelect: () => void downloadAs('docx'),
    },
    { label: 'Download as PDF', icon: <Icons.Download />, onSelect: () => void downloadAs('pdf') },
    {
      label: 'Rename',
      icon: <Icons.Pencil />,
      shortcut: 'F2',
      separatorBefore: true,
      onSelect: () => {
        setDraft(record.name);
        setRenaming(true);
      },
    },
    {
      label: 'Duplicate',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+D',
      onSelect: () => void duplicate(),
      separatorBefore: true,
    },
    {
      label: 'Copy',
      icon: <Icons.Copy />,
      shortcut: 'Ctrl+C',
      onSelect: () => clipboard.copy(payloadFor('document', record.id)),
    },
    {
      label: 'Cut',
      icon: <Icons.Scissors />,
      shortcut: 'Ctrl+X',
      onSelect: () => clipboard.cut(payloadFor('document', record.id)),
    },
    {
      label: 'Properties',
      icon: <InfoGlyph />,
      shortcut: 'Alt+Enter',
      onSelect: showProperties,
    },
    {
      label: 'Move to trash',
      icon: <Icons.Trash />,
      shortcut: 'Del',
      tone: 'danger',
      separatorBefore: true,
      onSelect: () => void remove(),
    },
  ];

  /*
   * A tile in the grid.
   *
   * Everything above this — the menu, the rename, the dialogs — is the same
   * whichever way the Drive is being shown; only the shape differs. A letter
   * lives in the database rather than as a file, so there is no picture to
   * show and the tile shows what it is instead.
   */
  if (view === 'grid') {
    return (
      <>
        <li
          onContextMenu={menu.openAtPointer}
          onClick={(event) => {
            if (renaming) return;
            if ((event.target as HTMLElement).closest('button, input, a, label')) return;
            if (selectionActive) {
              onToggleSelected?.();
              return;
            }
            navigate(`/documents/${record.id}`);
          }}
          className={cx(
            'group relative flex cursor-pointer flex-col gap-2 rounded-xl border p-2.5 transition-fast',
            selected
              ? 'border-[var(--blue-600)] bg-blue-50'
              : 'border-border bg-surface hover:bg-blue-50',
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {onToggleSelected ? (
              <span
                className={cx(
                  'shrink-0 transition-fast',
                  selected || selectionActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
              >
                <SelectBox
                  checked={Boolean(selected)}
                  label={`Select ${record.name}`}
                  onChange={onToggleSelected}
                />
              </span>
            ) : null}

            <span className="shrink-0" aria-hidden>
              <BrandIcon src={BRAND.word} size={20} />
            </span>

            <span
              className="min-w-0 flex-1 truncate text-body-sm font-medium text-ink"
              title={record.name}
            >
              {record.name}
            </span>

            <StarButton
              kind="document"
              id={record.id}
              name={record.name}
              starred={record.isStarred}
            />
            <IconButton label={`Actions for ${record.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>

          <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg bg-sunken">
            <BrandIcon src={BRAND.word} size={44} />
          </div>

          <p className="truncate text-micro text-muted">
            {pluralise(record.words, 'word')} · {formatRelative(record.updatedAt)}
          </p>
        </li>

        {menu.position ? (
          <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
        ) : null}
      </>
    );
  }

  return (
    <>
      <tr
        className={cx(
          'group h-row cursor-pointer border-b border-border transition-fast last:border-0 hover:bg-blue-50',
          selected && 'bg-blue-50',
        )}
        onContextMenu={menu.openAtPointer}
        // The whole row opens the document, not just its name.
        onClick={(event) => {
          if (renaming) return;
          if ((event.target as HTMLElement).closest('button, input, a, label')) return;

          // Extend the selection rather than navigating out of it.
          if (selectionActive) {
            onToggleSelected?.();
            return;
          }

          navigate(`/documents/${record.id}`);
        }}
        draggable={!renaming}
        onDragStart={(event) =>
          startDrag(event, { kind: 'document', id: record.id, name: record.name })
        }
      >
        <td className="pl-4">
          {onToggleSelected ? (
            <SelectBox
              checked={Boolean(selected)}
              label={`Select ${record.name}`}
              onChange={onToggleSelected}
            />
          ) : null}
        </td>

        <td className="px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="shrink-0" aria-hidden>
              <BrandIcon src={BRAND.word} size={20} />
            </span>

            {renaming ? (
              <input
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={() => void commitRename()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void commitRename();
                  if (event.key === 'Escape') {
                    setDraft(record.name);
                    setRenaming(false);
                  }
                }}
                aria-label="Document name"
                className="h-8 w-64 rounded-md border border-[var(--blue-600)] px-2 text-body-sm text-ink outline-none"
              />
            ) : (
              <Link
                to={`/documents/${record.id}`}
                title={record.name}
                className="truncate text-body-sm font-medium text-ink hover:text-blue-700 hover:underline"
              >
                {record.name}
              </Link>
            )}
          </div>

          {/* The two columns that the narrow layout drops, returned as a
              second line — the same fold the Drive's own rows do. Without it
              these cells were still drawn into a column the colgroup had
              squeezed to nothing, and the date printed straight over the
              name. */}
          <p className="mt-0.5 truncate pl-[30px] text-micro text-muted md:hidden">
            {pluralise(record.words, 'word')} · {formatDateTime(record.updatedAt)}
          </p>
        </td>

        <td className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden">
          {pluralise(record.words, 'word')}
        </td>

        <td
          className="whitespace-nowrap px-4 text-right text-body-sm text-muted max-md:hidden"
          data-numeric
        >
          {formatDateTime(record.updatedAt)}
        </td>

        <td className="px-4">
          <div className="flex items-center justify-end gap-0.5">
            <StarButton
              kind="document"
              id={record.id}
              name={record.name}
              starred={record.isStarred}
            />
            <IconButton label={`Actions for ${record.name}`} onClick={menu.openUnder}>
              <KebabGlyph />
            </IconButton>
          </div>
        </td>
      </tr>

      {menu.position ? (
        <ContextMenu items={menuItems} position={menu.position} onClose={menu.close} />
      ) : null}

      {previewing ? (
        <PdfPreview
          title={record.name}
          url={pdfUrl}
          onClose={closePreview}
          onDownload={() => {
            if (!pdfUrl) return;
            const anchor = window.document.createElement('a');
            anchor.href = pdfUrl;
            anchor.download = `${record.name}.pdf`;
            anchor.click();
          }}
        />
      ) : null}
    </>
  );
}

function PrinterGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      <path d="M5 7V3h8v4M5 13H3.5V8.5h11V13H13M5 11h8v4H5z" />
    </svg>
  );
}

function InfoGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 8v4M9 5.8v.2" strokeLinecap="round" />
    </svg>
  );
}

function KebabGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden>
      <circle cx="9" cy="4" r="1.4" />
      <circle cx="9" cy="9" r="1.4" />
      <circle cx="9" cy="14" r="1.4" />
    </svg>
  );
}
